import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Card, PageHeader, Btn, Field, inputCls, StatusPill, Empty, RadioGroupPills } from "../../components/dash/Kit";
import { api, errMessage } from "../../lib/api";

const empty = {
  title: "", description: "", assignee_type: "customer", customer_id: "",
  assignee_id: "", due_date: "", priority: "Normal",
};

export default function AdminTasks() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [filter, setFilter] = useState("");
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(false);

  const load = (status) =>
    api.get("/admin/tasks", { params: status ? { status } : {} }).then((r) => setRows(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/admin/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(filter); }, [filter]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async () => {
    const n = {};
    if (!form.title.trim()) n.title = "Please enter a task title.";
    if (form.assignee_type === "customer" && !form.customer_id) n.customer_id = "Please choose a customer.";
    if (form.assignee_type === "team" && !form.assignee_id) n.assignee_id = "Please choose a team member.";
    setErrors(n);
    if (Object.keys(n).length) return;

    setBusy(true);
    try {
      await api.post("/admin/tasks", form);
      toast.success("Task assigned and the assignee has been emailed.");
      setForm(empty);
      load(filter);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const update = async (id, payload) => {
    try {
      await api.put(`/admin/tasks/${id}`, payload);
      toast.success("Task updated.");
      setNotes((s) => ({ ...s, [id]: "" }));
      load(filter);
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/tasks/${id}`);
      toast.success("Task deleted.");
      load(filter);
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-tasks-page">
      <PageHeader
        title="Tasks"
        body="Assign work to a customer (materials you need from them) or to a team member. Everyone gets an email."
        action={
          <select data-testid="admin-task-filter" className={`${inputCls} sm:w-52`} value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All statuses</option>
            {["Open", "In Progress", "Submitted", "Completed"].map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
        }
      />

      <Card testId="admin-task-form">
        <p className="font-display text-lg font-semibold text-slate-900">Assign a new task</p>

        <div className="mt-5">
          <Field label="Assign to">
            <RadioGroupPills
              name="task-assignee-type"
              options={["customer", "team"]}
              value={form.assignee_type}
              onChange={(v) => setForm((f) => ({ ...f, assignee_type: v }))}
            />
          </Field>
        </div>

        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          {form.assignee_type === "customer" ? (
            <Field label="Customer" error={errors.customer_id}>
              <select data-testid="task-customer-select" className={inputCls} value={form.customer_id} onChange={set("customer_id")}>
                <option value="">Choose a customer</option>
                {customers.map((c) => (<option key={c.id} value={c.id}>{c.business_name || c.email}</option>))}
              </select>
            </Field>
          ) : (
            <Field label="Team member" error={errors.assignee_id}>
              <select data-testid="task-team-select" className={inputCls} value={form.assignee_id} onChange={set("assignee_id")}>
                <option value="">Choose a team member</option>
                {team.map((t) => (<option key={t.id} value={t.id}>{t.first_name} {t.last_name} ({t.email})</option>))}
              </select>
            </Field>
          )}

          <Field label="Task title" error={errors.title} htmlFor="task-title">
            <input id="task-title" data-testid="task-title-input" className={inputCls} value={form.title} onChange={set("title")} placeholder="Upload 5 photos of the new menu" />
          </Field>
        </div>

        <div className="mt-5">
          <Field label="Description" htmlFor="task-description">
            <textarea id="task-description" rows={3} data-testid="task-description-input" className={inputCls} value={form.description} onChange={set("description")} />
          </Field>
        </div>

        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <Field label="Due date" htmlFor="task-due">
            <input id="task-due" type="date" data-testid="task-due-input" className={inputCls} value={form.due_date} onChange={set("due_date")} />
          </Field>
          <Field label="Priority">
            <RadioGroupPills
              name="task-priority"
              options={["Normal", "High"]}
              value={form.priority}
              onChange={(v) => setForm((f) => ({ ...f, priority: v }))}
            />
          </Field>
        </div>

        <Btn testId="create-task-button" className="mt-6" onClick={create} disabled={busy}>
          {busy ? "Assigning…" : "Assign Task"}
        </Btn>
      </Card>

      {rows.length === 0 ? (
        <Empty testId="admin-tasks-empty" title="No tasks yet" body="Assign the first task above." />
      ) : (
        <div className="space-y-3" data-testid="admin-tasks-list">
          {rows.map((t) => (
            <Card key={t.id} testId={`admin-task-${t.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display text-base font-semibold text-slate-900">{t.title}</p>
                  <p className="text-xs text-slate-500">
                    {t.assignee_type === "customer"
                      ? `Customer: ${t.customer?.business_name || t.customer?.email || "—"}`
                      : `Team: ${t.assignee?.email || "—"}`}
                    {t.due_date ? ` · due ${t.due_date}` : ""} · {t.priority}
                    {t.media_ids?.length ? ` · ${t.media_ids.length} file(s)` : ""}
                  </p>
                  {t.description && <p className="mt-2 text-sm text-slate-600">{t.description}</p>}
                  {t.comments?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {t.comments.map((c) => (
                        <div key={c.id} className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-700">
                          {c.note}
                          <span className="block text-xs text-slate-400">
                            {c.author} · {new Date(c.created_at).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status={t.status} />
                  <Btn variant="danger" testId={`admin-task-delete-${t.id}`} onClick={() => remove(t.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Btn>
                </div>
              </div>

              <div className="mt-4">
                <Field label="Reply to the assignee" htmlFor={`admin-task-note-${t.id}`}>
                  <textarea
                    id={`admin-task-note-${t.id}`}
                    rows={2}
                    data-testid={`admin-task-note-input-${t.id}`}
                    className={inputCls}
                    value={notes[t.id] || ""}
                    onChange={(e) => setNotes((s) => ({ ...s, [t.id]: e.target.value }))}
                  />
                </Field>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Btn variant="outline" testId={`admin-task-note-${t.id}`} onClick={() => update(t.id, { note: notes[t.id] })}>
                  Send note
                </Btn>
                <Btn variant="outline" testId={`admin-task-progress-${t.id}`} onClick={() => update(t.id, { status: "In Progress" })}>
                  In progress
                </Btn>
                <Btn testId={`admin-task-complete-${t.id}`} onClick={() => update(t.id, { status: "Completed", note: notes[t.id] })}>
                  Mark completed
                </Btn>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
